/**
 * linearItemSync — an item linked to a Linear issue, and what comes over.
 *
 * Linking records the issue on the item (entity_external_ids: the issue's id
 * as the join key, its key such as PRI-12 as the label, and its link), then
 * brings the issue's fields in through the workspace's Linear import mapping
 * set (Settings → Field Mappings). Until the workspace maps otherwise that set
 * is the Linear default: the description, and the priority into the custom
 * field "Priority" — created in the workspace the first time it is needed.
 *
 * Syncing again (by hand, when the item page opens and the link is stale, or
 * after an import) re-reads the issue and writes a field only when Linear's
 * value changed since the last sync, or the item's field is empty — so an
 * edit made here is not undone by an issue that did not change. What Linear
 * last said is kept at rest in item_source_attributes (migration 720), which
 * is also what makes "changed since last sync" answerable.
 *
 * The Linear import of a team or project runs the same routine for every
 * item it creates; linking and importing never disagree on what comes over.
 */

import { useAttributesStore } from '../stores/prioritization/attributesStore';
import type { Attribute } from '../types/prioritization/lifecycle';
import {
  getDefaultLinearMappingSet,
  LINEAR_PRIORITY_FIELD_KEY,
  LINEAR_PRIORITY_VALUE_MAP,
  loadFieldMappingConfig,
  saveFieldMappingConfig,
  type FieldMappingConfig,
  type FieldMappingSet,
} from './fieldMappingRegistry';
import { customFieldKeyOfTarget, resolveActiveMappingSet } from './fieldMappingApply';
import { deactivateExternalId, logChangeEvent, notifyExternalIdsChanged, registerExternalId } from './connectors';
import { linearIssue, type LinearIssueSummary } from './semanticConnectors/linearSemanticApi';
import {
  applyLinkedValuesToItem,
  planLinkedSync,
  type LinkedSyncDecision,
  type LinkedSyncPlan,
} from './linkedItemSync';

export const LINEAR_CONNECTOR = 'linear';

/** A link older than this is re-read when its item page opens. */
export const LINEAR_AUTO_SYNC_AGE_MS = 10 * 60 * 1000;

/** The options the custom field "Priority" is created with — Linear's five, in Linear's order. */
export const LINEAR_PRIORITY_OPTIONS: readonly string[] = LINEAR_PRIORITY_VALUE_MAP.map((v) => v.internalValue);

// ── The issue, as external values ─────────────────────────────────────────────

/**
 * An issue keyed the way CONNECTOR_FIELDS.linear names its fields, so the
 * workspace's Linear rules read it. Priority is the number Linear sends (the
 * value list names it); state and labels are the names a person sees.
 */
export function linearIssueExternalValues(issue: LinearIssueSummary): Record<string, unknown> {
  return {
    identifier: issue.identifier,
    title: issue.title,
    description: issue.description ?? '',
    priority: String(issue.priority ?? 0),
    priority_label: issue.priorityLabel,
    state: issue.state?.name ?? '',
    labels: issue.labels ?? [],
    team: issue.team?.key ?? issue.team?.name ?? '',
    project: issue.project?.name ?? '',
    estimate: issue.estimate ?? '',
    assignee: issue.assignee?.email ?? '',
    due_date: issue.dueDate ?? '',
    url: issue.url,
    updated_at: issue.updatedAt,
  };
}

// ── The plan (pure) ───────────────────────────────────────────────────────────

export type LinearSyncDecision = LinkedSyncDecision;
export type LinearSyncPlan = LinkedSyncPlan;

/** Decide what a Linear sync writes — the shared plan (linkedItemSync), with Linear named in its reasons. */
export function planLinearSync(params: Omit<Parameters<typeof planLinkedSync>[0], 'systemName'>): LinearSyncPlan {
  return planLinkedSync({ systemName: 'Linear', ...params });
}

/** A link is stale when it was never synced or its last sync is older than the auto-sync age. */
export function linearLinkIsStale(lastSyncedAt: string | null | undefined, now: number = Date.now()): boolean {
  if (!lastSyncedAt) return true;
  const at = new Date(lastSyncedAt).getTime();
  return !Number.isFinite(at) || now - at > LINEAR_AUTO_SYNC_AGE_MS;
}

// ── The workspace's Linear mapping set and the Priority field ─────────────────

/**
 * The import set that applies to Linear. When the workspace has none, the
 * Linear default is saved into its connector config and picked, so Settings →
 * Field Mappings shows the rules that run from then on.
 */
export async function ensureLinearMappingSet(
  workspaceId: string,
): Promise<{ set: FieldMappingSet; created: boolean }> {
  const config = await loadFieldMappingConfig(workspaceId, LINEAR_CONNECTOR);
  const active = resolveActiveMappingSet(config, 'import');
  if (active.set) return { set: active.set, created: false };
  const set = getDefaultLinearMappingSet();
  const next: FieldMappingConfig = {
    ...config,
    version: 3,
    sets: [...config.sets.filter((s) => s.id !== set.id), set],
    importSetId: set.id,
  };
  const { error } = await saveFieldMappingConfig(workspaceId, LINEAR_CONNECTOR, next);
  if (error) throw new Error(`The Linear mapping set could not be saved: ${error}`);
  return { set, created: true };
}

/**
 * The custom field "Priority" (key `priority`) that the default set writes to:
 * created as a select with Linear's five values when the workspace has no
 * field of that key; given any of the five values it lacks when it has one.
 * A scoring attribute of that key is left alone and reported.
 */
export async function ensureLinearPriorityField(): Promise<{
  created: boolean;
  extended: string[];
  /** True when `priority` is a scoring attribute, not a custom field — the rule cannot write it. */
  conflict: boolean;
  error?: string;
}> {
  const store = useAttributesStore.getState();
  if (store.attributes.length === 0) await store.fetchFromSupabase();
  const existing = useAttributesStore.getState().attributes.find((a) => a.key === LINEAR_PRIORITY_FIELD_KEY);
  if (!existing) {
    const field: Attribute = {
      key: LINEAR_PRIORITY_FIELD_KEY,
      label: 'Priority',
      definition: 'The priority of the linked Linear issue — Urgent, High, Medium, Low or No priority. Copied from Linear when the item is linked or synced.',
      type: 'select',
      required: false,
      options: [...LINEAR_PRIORITY_OPTIONS],
      isCustomField: true,
      enabled: true,
      objectType: 'item',
      customDisplayType: 'dropdown',
      placement: { tab: 'overview', glance: true, quickPanel: false, listColumn: false },
    } as Attribute;
    try {
      const result = await store.addAttribute(field);
      if (result.error) return { created: false, extended: [], conflict: false, error: String(result.error) };
      return { created: true, extended: [], conflict: false };
    } catch (err) {
      return { created: false, extended: [], conflict: false, error: err instanceof Error ? err.message : 'Priority field not created' };
    }
  }
  if (!existing.isCustomField) return { created: false, extended: [], conflict: true };
  if (existing.type !== 'select' && existing.type !== 'multiselect') return { created: false, extended: [], conflict: false };
  const missing = LINEAR_PRIORITY_OPTIONS.filter((o) => !(existing.options ?? []).includes(o));
  if (missing.length === 0) return { created: false, extended: [], conflict: false };
  const result = await store.updateAttribute(existing.key, { options: [...(existing.options ?? []), ...missing] });
  if (result.error) return { created: false, extended: [], conflict: false, error: String(result.error) };
  return { created: false, extended: missing, conflict: false };
}

// ── Sync ──────────────────────────────────────────────────────────────────────

export type LinearSyncReason = 'link' | 'now' | 'auto' | 'import';

export interface LinearSyncResult {
  ok: boolean;
  issue?: { id: string; identifier: string; url: string; title: string };
  /** Field labels this sync wrote. */
  wrote: string[];
  /** Field labels left as they were, with why. */
  kept: Array<{ label: string; reason: string }>;
  /** Rules that could not run, with why. */
  skipped: Array<{ label: string; reason: string }>;
  errors: string[];
  fieldCreated: boolean;
  setCreated: boolean;
}

/** The issue an apply is about — enough to label the link and stamp the sync. */
export interface LinearIssueRef {
  id: string;
  identifier: string;
  url: string;
  title: string;
  updatedAt: string;
  teamKey?: string;
}

/**
 * Re-read a linked issue and bring its mapped fields onto the item. Pass
 * `issue` when the caller already read it (linking); otherwise it is read
 * from Linear now.
 */
export async function syncLinkedLinearIssue(params: {
  workspaceId: string;
  itemId: string;
  issueId: string;
  userId?: string | null;
  reason: LinearSyncReason;
  issue?: LinearIssueSummary;
}): Promise<LinearSyncResult> {
  const { workspaceId, itemId, userId, reason } = params;
  let issue: LinearIssueSummary;
  try {
    issue = params.issue ?? (await linearIssue(workspaceId, params.issueId));
  } catch (err) {
    return {
      ok: false,
      wrote: [],
      kept: [],
      skipped: [],
      errors: [err instanceof Error ? err.message : 'Linear could not be read'],
      fieldCreated: false,
      setCreated: false,
    };
  }
  return applyLinearValuesToItem({
    workspaceId,
    itemId,
    userId,
    reason,
    issue: { id: issue.id, identifier: issue.identifier, url: issue.url, title: issue.title, updatedAt: issue.updatedAt, teamKey: issue.team?.key },
    values: linearIssueExternalValues(issue),
  });
}

/**
 * Bring one issue's values (already read — by a sync or by the import) onto
 * one item through the Linear mapping set, record what Linear said, and stamp
 * the link. The one routine behind linking, Sync now, the stale-link re-read
 * and the Linear import.
 */
export async function applyLinearValuesToItem(params: {
  workspaceId: string;
  itemId: string;
  userId?: string | null;
  reason: LinearSyncReason;
  issue: LinearIssueRef;
  values: Record<string, unknown>;
}): Promise<LinearSyncResult> {
  const { workspaceId, itemId, userId, reason, issue, values } = params;
  const result: LinearSyncResult = { ok: false, wrote: [], kept: [], skipped: [], errors: [], fieldCreated: false, setCreated: false };
  result.issue = { id: issue.id, identifier: issue.identifier, url: issue.url, title: issue.title };

  let set: FieldMappingSet;
  try {
    const ensured = await ensureLinearMappingSet(workspaceId);
    set = ensured.set;
    result.setCreated = ensured.created;
  } catch (err) {
    result.errors.push(err instanceof Error ? err.message : 'The Linear mapping set could not be read');
    return result;
  }

  // The rule that writes the custom field "Priority" gets its field.
  const writesPriorityField = set.entries.some(
    (e) => e.enabled && customFieldKeyOfTarget(e.internalFieldKey) === LINEAR_PRIORITY_FIELD_KEY,
  );
  if (writesPriorityField) {
    const field = await ensureLinearPriorityField();
    result.fieldCreated = field.created;
    if (field.error) result.errors.push(field.error);
    if (field.conflict) {
      result.skipped.push({ label: 'Priority', reason: '“priority” is a scoring attribute in this workspace, not a custom field; map Linear’s priority to it as an attribute instead' });
    }
  }

  const linked = await applyLinkedValuesToItem({
    connectorType: LINEAR_CONNECTOR,
    systemName: 'Linear',
    workspaceId,
    itemId,
    userId,
    reason,
    record: { id: issue.id, label: issue.identifier, url: issue.url, title: issue.title, updatedAt: issue.updatedAt, projectKey: issue.teamKey },
    values,
    set,
  });
  result.wrote.push(...linked.wrote);
  result.kept.push(...linked.kept);
  result.skipped.push(...linked.skipped);
  result.errors.push(...linked.errors);
  result.ok = result.errors.length === 0;
  return result;
}

/** Link an item to a Linear issue, then bring the issue's fields over. */
export async function linkItemToLinearIssue(params: {
  workspaceId: string;
  itemId: string;
  issue: LinearIssueSummary;
  userId?: string | null;
}): Promise<LinearSyncResult> {
  const { workspaceId, itemId, issue, userId } = params;
  await registerExternalId({
    workspaceId,
    entityType: 'item',
    internalId: itemId,
    connectorType: LINEAR_CONNECTOR,
    externalId: issue.id,
    externalLabel: issue.identifier,
    externalUrl: issue.url,
    externalProjectKey: issue.team?.key,
  });
  void logChangeEvent({
    workspaceId,
    entityType: 'item',
    entityId: itemId,
    eventType: 'updated',
    fieldName: 'linear_link',
    newValue: { issue: issue.identifier, url: issue.url },
    syncStatus: 'synced',
  });
  return syncLinkedLinearIssue({ workspaceId, itemId, issueId: issue.id, userId, reason: 'link', issue });
}

/** Remove the link (the row stays, inactive). The fields already brought over stay on the item. */
export async function unlinkItemFromLinear(params: {
  workspaceId: string;
  itemId: string;
  externalId: string;
}): Promise<{ error: string | null }> {
  const outcome = await deactivateExternalId({
    workspaceId: params.workspaceId,
    entityType: 'item',
    internalId: params.itemId,
    connectorType: LINEAR_CONNECTOR,
    externalId: params.externalId,
  });
  if (!outcome.error) notifyExternalIdsChanged('item', params.itemId);
  return outcome;
}

/** One line for a toast or a panel: "Brought over Description and Priority from PRI-12." */
export function describeLinearSync(result: LinearSyncResult): string {
  const key = result.issue?.identifier ?? 'the Linear issue';
  if (!result.ok && result.errors.length > 0) return `Sync with ${key} did not finish: ${result.errors[0]}`;
  if (result.wrote.length === 0) return `Nothing new from ${key} — the item already matches it.`;
  const list = result.wrote.length === 1 ? result.wrote[0] : `${result.wrote.slice(0, -1).join(', ')} and ${result.wrote[result.wrote.length - 1]}`;
  return `Brought over ${list} from ${key}.`;
}
